// Text table DE/EN (AD-18, AD-34). Reason codes and machine states come from the protocol catalogs; unknown codes are
// shown neutrally. German is the default, as in the local app.
import { machineStateCatalog, reasonCatalog } from '@brewcore/protocol';
import { appReasonCatalog } from '@brewcore/app-contract';
import { calmAgeSeconds } from './calm.js';
import { DAILY_TEXTS } from './texts-daily.js';
// Claude support (src/ai, feature/claude-assist)
import { AI_TEXTS } from '../ai/texts.js';
// Detail parity with the local app (feature/app-parity)
import { PARITY_TEXTS } from './texts-parity.js';
// The screens of the prototype canvas, 1:1 (21.10.2026)
import { PROTO_TEXTS } from './texts-proto.js';

export type Lang = 'de' | 'en';

const TEXTS = {
  ...DAILY_TEXTS,
  ...AI_TEXTS,
  ...PARITY_TEXTS,
  ...PROTO_TEXTS,
  title: { de: 'Brewcore', en: 'Brewcore' },
  loading: { de: 'Lädt …', en: 'Loading …' },
  // a list of cloud data that did not load: the phone did not reach the cloud (the http layer's "offline")
  listOffline: {
    de: 'Keine Verbindung zur Cloud. Liste lädt, sobald die Verbindung zurück ist.',
    en: 'No connection to the cloud. The list loads once the connection is back.',
  },
  notLoggedIn: { de: 'Nicht angemeldet. Bitte anmelden.', en: 'Not signed in. Please sign in.' },
  loginTitle: { de: 'Anmelden', en: 'Sign in' },
  // the brand already stands above it in the logo (Dominik 21.10.2026, canvas "Anmelden")
  loginHeadline: { de: 'Anmelden', en: 'Sign in' },
  loginIntro: {
    de: 'Bitte die eingeladene E-Mail-Adresse eingeben. Ein Code kommt per Mail.',
    en: 'Please enter the email address that was invited. A code will follow by email.',
  },
  emailLabel: { de: 'E-Mail-Adresse', en: 'Email address' },
  sendCode: { de: 'Code senden', en: 'Send code' },
  codeSent: {
    de: 'Ist {email} eingeladen, kommt gleich ein Code. Er gilt 15 Minuten auf diesem Gerät.',
    en: 'If {email} is invited, a code is coming. It works for 15 minutes on this device.',
  },
  codeTitle: { de: 'Code eingeben', en: 'Enter the code' },
  codeLabel: { de: 'Code aus der Mail', en: 'Code from the email' },
  signIn: { de: 'Anmelden', en: 'Sign in' },
  otherAddress: { de: 'Andere Adresse', en: 'Use another address' },
  resendCode: { de: 'Neuen Code senden', en: 'Send new code' },
  attemptsLeft: { de: 'Noch {n} Versuche.', en: '{n} attempts left.' },
  retryIn: { de: 'Bitte in {n} s noch einmal versuchen.', en: 'Please try again in {n} s.' },
  // behind the (i) of the code's label (board "Anmelden (Code)")
  noMailInfo: {
    de: 'Keine Mail? Bitte Spam-Ordner prüfen.',
    en: 'No email? Please check the spam folder.',
  },
  or: { de: 'oder', en: 'or' },
  google: { de: 'Mit Google anmelden', en: 'Continue with Google' },
  passkeyLogin: { de: 'Mit Passkey anmelden', en: 'Use a passkey' },
  networkError: {
    de: 'Keine Verbindung zur Cloud. Bitte gleich noch einmal versuchen.',
    en: 'No connection to the cloud. Please try again in a moment.',
  },
  loginFailed: { de: 'Anmeldung fehlgeschlagen.', en: 'Sign-in failed.' },
  sessionEnded: {
    de: 'Abgemeldet. Bitte neu anmelden.',
    en: 'Signed out. Please sign in again.',
  },
  account: { de: 'Konto', en: 'Account' },
  logout: { de: 'Abmelden', en: 'Sign out' },
  logoutAll: { de: 'Überall abmelden', en: 'Sign out everywhere' },
  // the account sheet's rows (prototype "Konto"): the language as the row's value, the link over the devices
  languageRow: { de: 'Sprache', en: 'Language' },
  languageNow: { de: 'Deutsch', en: 'English' },
  devices: { de: 'Angemeldete Geräte', en: 'Signed-in devices' },
  thisDevice: { de: 'dieses Gerät', en: 'this device' },
  endSession: { de: 'Abmelden', en: 'Sign out' },
  lastActive: { de: 'zuletzt aktiv', en: 'last active' },
  passkeyAdd: { de: 'Passkey einrichten', en: 'Add a passkey' },
  passkeyAdded: { de: 'Passkey gespeichert.', en: 'Passkey saved.' },
  freshNeeded: {
    de: 'Letzte Anmeldung ist älter als 15 Minuten. Bitte neu anmelden.',
    en: 'Last sign-in was over 15 minutes ago. Please sign in again.',
  },
  linkGoogle: { de: 'Google verknüpfen', en: 'Link Google' },
  method_email: { de: 'Code per Mail', en: 'Code by email' },
  method_google: { de: 'Google', en: 'Google' },
  method_passkey: { de: 'Passkey', en: 'Passkey' },
  method_dev: { de: 'Entwickler', en: 'Developer' },
  operator: { de: 'Betreiber', en: 'Operator' },
  // a card's title, no full stop (board "Erster Start"); it stood as a sentence and broke over two lines
  noMachines: { de: 'Noch keine Maschine gekoppelt', en: 'No machine paired yet' },
  net_connecting: { de: 'Verbindet mit der Cloud …', en: 'Connecting to the cloud …' },
  net_ok: { de: 'Cloud verbunden', en: 'Cloud connected' },
  net_cloud_unreachable: { de: 'Cloud nicht erreichbar', en: 'Cloud not reachable' },
  net_device_offline: { de: 'Handy ohne Internet', en: 'No internet on this device' },
  conn_unknown: { de: 'Verbindung unbekannt', en: 'Connection unknown' },
  conn_loading: { de: 'verbindet …', en: 'connecting …' },
  conn_online: { de: 'online', en: 'online' },
  conn_reconnecting: { de: 'verbindet neu …', en: 'reconnecting …' },
  conn_offline: { de: 'offline', en: 'offline' },
  conn_restarting: { de: 'startet neu …', en: 'restarting …' },
  // this device has no connection: the machine's state is unknown (the cloud bar says why, the note since when)
  conn_link_down: { de: 'Status unbekannt', en: 'status unknown' },
  lastSeen: { de: 'zuletzt gesehen', en: 'last seen' },
  never: { de: 'nie', en: 'never' },
  ago_s: { de: 'vor {n} s', en: '{n} s ago' },
  ago_min: { de: 'vor {n} min', en: '{n} min ago' },
  ago_h: { de: 'vor {n} h', en: '{n} h ago' },
  temperature: { de: 'Temperatur', en: 'Temperature' },
  target: { de: 'Soll', en: 'Target' },
  noLive: { de: 'Keine Live-Werte', en: 'No live values' },
  // the last live value after a short gap, dimmed (core/calm.ts): "Stand vor 20 s"
  liveAsOf: { de: 'Stand {ago}', en: 'updated {ago}' },
  state: { de: 'Zustand', en: 'State' },
  setpoint: { de: 'Temperatur', en: 'Temperature' },
  decrease: { de: 'weniger', en: 'decrease' },
  increase: { de: 'mehr', en: 'increase' },
  standby: { de: 'Standby', en: 'Standby' },
  // short: the two buttons under the ring share a 360 px line ("In Standby schalten" broke into two lines)
  standbyOn: { de: 'Standby', en: 'Standby' },
  standbyOff: { de: 'Einschalten', en: 'Switch on' },
  flush: { de: 'Spülen', en: 'Flush' },
  // one question for flushing wherever it starts (machine, care, the note after a shot): the question as the title,
  // the button with the same verb (WRITING.md 2, 23.10.2026)
  flushConfirmTitle: { de: 'Jetzt spülen?', en: 'Flush now?' },
  flushGo: { de: 'Spülen', en: 'Flush' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  sending: { de: 'wird gesendet …', en: 'sending …' },
  running: { de: 'läuft …', en: 'running …' },
  saved: { de: 'gespeichert', en: 'saved' },
  result_done: { de: 'erledigt', en: 'done' },
  result_failed: { de: 'fehlgeschlagen', en: 'failed' },
  result_rejected: { de: 'abgelehnt', en: 'rejected' },
  result_expired: { de: 'abgelaufen', en: 'expired' },
  result_stale: { de: 'nicht mehr aktuell', en: 'out of date' },
  result_no_response: { de: 'keine Rückmeldung', en: 'no response' },
  result_unconfirmed: { de: 'nicht bestätigt', en: 'not confirmed' },
  dataLost: {
    de: 'Maschine hat Daten verloren. Bitte Einstellungen prüfen.',
    en: 'Machine has lost data. Please check its settings.',
  },
  remoteLocked: {
    de: 'Fernsteuerung ist gesperrt. An der Maschine funktioniert alles normal.',
    en: 'Remote control is locked. Everything works as usual at the machine.',
  },
  language: { de: 'English', en: 'Deutsch' },
  pair: { de: 'Maschine koppeln', en: 'Pair a machine' },
  // board "Maschine koppeln": the label short, the format behind its (i)
  pairCodeLabel: { de: 'Kopplungscode', en: 'Pairing code' },
  pairCodeInfo: { de: '8 Zeichen, zum Beispiel 7K3Q-X9TB.', en: '8 characters, for example 7K3Q-X9TB.' },
  pairCodeFormat: { de: 'Code muss 8 Zeichen haben.', en: 'Code needs 8 characters.' },
  pairNext: { de: 'Weiter', en: 'Next' },
  pairQuestion: {
    de: 'Diese Maschine mit dem Konto {account} koppeln?',
    en: 'Pair this machine with the account {account}?',
  },
  pairMachineInfo: { de: '{type}, Firmware {fw}', en: '{type}, firmware {fw}' },
  pairName: { de: 'Name der Maschine', en: 'Machine name' },
  pairReinstall: {
    de: 'Das ist vermutlich „{name}“ nach einer Neuinstallation. Verlauf und Daten übernehmen.',
    en: 'This is probably “{name}” after a reinstall. Carry over its history and data.',
  },
  pairReinstallRecent: {
    de: '„{name}“ war vor Kurzem online. Übernahme ist erst 10 Minuten danach möglich.',
    en: '“{name}” was online recently. Data can be carried over 10 minutes later.',
  },
  pairBlocked: {
    de: 'Maschine ist mit einem anderen Konto gekoppelt. Bitte dort zuerst entkoppeln.',
    en: 'Machine is paired with another account. Please unpair it there first.',
  },
  pairSubmit: { de: 'Koppeln', en: 'Pair' },
  pairWorking: { de: 'Einen Moment …', en: 'One moment …' },
  pairDone: {
    de: '„{name}“ ist gekoppelt. Maschine verbindet sich gleich.',
    en: '“{name}” is paired. Machine will connect in a moment.',
  },
  pairTooMany: {
    de: 'Zu viele Versuche. Bitte in {min} min noch einmal versuchen.',
    en: 'Too many attempts. Please try again in {min} min.',
  },
  close: { de: 'Schließen', en: 'Close' },
  unpair: { de: 'Entkoppeln', en: 'Unpair' },
  unpairTitle: { de: 'Maschine entkoppeln?', en: 'Unpair this machine?' },
  unpairText: {
    de: 'Mitnutzer verlieren den Zugriff. Bezüge bleiben erhalten.',
    en: 'Members lose access. Shots are kept.',
  },
  // design "Glut" (18.10.2026): shell, first run, pairing flow, machine home, account sheet
  hello: { de: 'Hallo {name}', en: 'Hello {name}' },
  welcome: { de: 'Willkommen', en: 'Welcome' },
  firstRunText: {
    de: 'Temperatur, Rezept und Zeitplan erscheinen nach dem Koppeln.',
    en: 'Temperature, recipe and schedule appear after pairing.',
  },
  firstRunStep1: {
    de: 'Orione einschalten und mit dem WLAN verbinden.',
    en: 'Turn on the Orione and connect to Wi-Fi.',
  },
  firstRunStep2: {
    de: 'Warten, bis das Display einen Code zeigt.',
    en: 'Wait until the display shows a code.',
  },
  firstRunStep3: {
    de: 'Code hier eingeben oder QR-Code scannen.',
    en: 'Enter the code or scan the QR code.',
  },
  firstRunSteps: { de: 'So geht’s', en: 'How it works' },
  inviteHint: { de: 'Eingeladen? Link in der Mail öffnen', en: 'Invited? Open the link in the email' },
  loadingMachines: { de: 'Maschinen laden …', en: 'Loading machines …' },
  pairStep: { de: 'Schritt {n} von {m}', en: 'Step {n} of {m}' },
  // the same where the title leaves no room for it (360 px)
  pairStepShort: { de: '{n}/{m}', en: '{n}/{m}' },
  pairCodeIntro: { de: 'Code vom runden Display eingeben.', en: 'Enter the code from the round display.' },
  pairDisplayLabel: { de: 'KOPPELN', en: 'PAIRING' },
  pairScan: { de: 'QR-Code scannen', en: 'Scan QR code' },
  pairScanHelp: {
    de: 'QR-Code am Display mit der Kamera scannen.',
    en: 'Scan the QR code on the display with the camera.',
  },
  pairFound: { de: 'Gefunden', en: 'Found' },
  pairFirmware: { de: 'Firmware {fw}', en: 'Firmware {fw}' },
  pairBack: { de: 'Zurück', en: 'Back' },
  pairWaitingTitle: { de: '{name} wird gekoppelt …', en: 'Pairing {name} …' },
  pairWaitCode: { de: 'Code bestätigt', en: 'Code confirmed' },
  pairWaitAccount: { de: 'Maschine wird dem Konto zugeordnet', en: 'Adding the machine to the account' },
  pairWaitHint: {
    de: 'Bitte App geöffnet lassen',
    en: 'Please keep the app open',
  },
  pairDoneTitle: { de: 'Gekoppelt', en: 'Paired' },
  pairToMachine: { de: 'Zur Maschine', en: 'Go to machine' },
  pairAnother: { de: 'Weitere Maschine koppeln', en: 'Pair another machine' },
  accountOpen: { de: 'Konto öffnen', en: 'Open account' },
  accountRole: { de: 'Rolle', en: 'Role' },
  user: { de: 'Nutzer', en: 'User' },
  cloudState: { de: 'Cloud', en: 'Cloud' },
  tabsLabel: { de: 'Bereiche', en: 'Sections' },
  tabMachine: { de: 'Maschine', en: 'Machine' },
  tabBrew: { de: 'Bezug', en: 'Brew' },
  tabSettings: { de: 'Einstellungen', en: 'Settings' },
  tabCare: { de: 'Wartung', en: 'Care' },
  machinesLabel: { de: 'Maschine wählen', en: 'Choose a machine' },
  stateReady: { de: 'Bereit', en: 'Ready' },
  stateHeating: { de: 'Heizt auf', en: 'Heating up' },
  stateCooling: { de: 'Kühlt ab', en: 'Cooling down' },
  // state 10 without fresh live values: whether it is ready or still heating is unknown
  stateOn: { de: 'Eingeschaltet', en: 'Switched on' },
  recipe: { de: 'Rezept', en: 'Recipe' },
  recipeEdit: { de: 'Ändern', en: 'Change' },
  noBean: { de: 'Ohne Bohne', en: 'No bean' },
  grind: { de: 'Mahlgrad {g}', en: 'Grind {g}' },
  stopTime: { de: 'Stopp nach {n} s', en: 'Stop after {n} s' },
  stopWeight: { de: 'Stopp bei {n} g', en: 'Stop at {n} g' },
  stopManual: { de: 'Stopp von Hand', en: 'Stop by hand' },
  preInfusionOn: { de: 'Pre-Infusion an', en: 'Pre-infusion on' },
  preInfusionOff: { de: 'Pre-Infusion aus', en: 'Pre-infusion off' },
  quickActions: { de: 'Schnellaktionen', en: 'Quick actions' },
  lastShot: { de: 'Letzter Bezug', en: 'Last shot' },
  weightLegend: { de: 'Gewicht', en: 'Weight' },
  flowLegend: { de: 'Durchfluss', en: 'Flow' },
  firstDrop: { de: 'erster Tropfen {n} s', en: 'first drop {n} s' },
} as const satisfies Record<string, Record<Lang, string>>;

export type TextKey = keyof typeof TEXTS;

// A number and its unit stay on one line ("15 Minuten", "~9 l", "vor 3 min"): the space between them is a
// non-breaking one (UI review 18.10.2026: "15 / Minuten" broke across lines). An estimate is "~" right before its number
// (Dominik 23.10.2026: "die tilde sollte in der gesamten app verwendet werden"), with no space to break at.
const UNIT_SPACE =
  /(\d) (?=(?:s|min|h|g|kg|l|ml|K|°C|%|g\/s|Minuten|Stunden|Sekunden|Tage|Bezügen|Bezüge|Versuche|Zeichen|Plätze|minutes|hours|seconds|days|shots|attempts|characters|digits)(?![\p{L}\d]))/gu;
// compounds that must not break at their hyphen ("QR-" / "Code"): non-breaking hyphen U+2011 (Barlow has it)
const KEEP_HYPHEN = /\b(QR|Pre)-(Code|Infusion|infusion)\b/g;

/** The line-break rules above, applied to every text t() returns. */
export const typeset = (s: string): string =>
  s.replace(UNIT_SPACE, '$1\u00a0').replace(KEEP_HYPHEN, '$1\u2011$2');

export function t(lang: Lang, key: TextKey, vars: Record<string, string | number> = {}): string {
  let s: string = TEXTS[key][lang];
  for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return typeset(s);
}

/** Like t() for keys that come as data (status notes): an unknown key shows as itself. */
export function tx(lang: Lang, key: string, vars: Record<string, string | number> = {}): string {
  return key in TEXTS ? t(lang, key as TextKey, vars) : key;
}

/** Text of a reason code (protocol v1 or app-v1 catalog); unknown codes neutrally as the code itself. */
export function reasonText(lang: Lang, code: string | undefined): string {
  if (!code) return '';
  const entry =
    reasonCatalog.reasons.find((r) => r.code === code) ??
    appReasonCatalog.reasons.find((r) => r.code === code);
  return entry ? entry[lang] : code;
}

/**
 * Why a list of cloud data (beans, shots, history) did not load. A GET of cloud data never needs the machine, so the
 * http layer's "offline" (no answer) means the phone did not reach the cloud, not "Maschine offline".
 */
export function listErrorText(lang: Lang, code: string | undefined): string {
  return code === 'offline' ? t(lang, 'listOffline') : reasonText(lang, code);
}

export function machineStateText(lang: Lang, code: number | undefined): string {
  if (code === undefined) return '–';
  const entry = machineStateCatalog.states.find((s) => s.code === code);
  return entry ? entry[lang] : String(code);
}

export function agoText(lang: Lang, fromMs: number | null, nowMs: number): string {
  if (fromMs === null) return t(lang, 'never');
  const s = Math.max(0, Math.round((nowMs - fromMs) / 1000));
  if (s < 90) return t(lang, 'ago_s', { n: s });
  const min = Math.round(s / 60);
  if (min < 90) return t(lang, 'ago_min', { n: min });
  return t(lang, 'ago_h', { n: Math.round(min / 60) });
}

/** An age in the calm steps of core/calm.ts ("vor 20 s", "vor 3 min"): changes every 5 s, not every second. */
export function calmAgoText(lang: Lang, ageMs: number): string {
  const a = calmAgeSeconds(ageMs);
  return t(lang, a.unit === 's' ? 'ago_s' : a.unit === 'min' ? 'ago_min' : 'ago_h', { n: a.n });
}

export function textKeys(): TextKey[] {
  return Object.keys(TEXTS) as TextKey[];
}
